<template>
  <div>
    <nav-bar :title="navBarTitle" />
    <div class="mobile none-padding-mobile pt-4 position-relative">
      <b-container>
        <b-skeleton-wrapper :loading="loading">
          <template #loading>
            <calendar-skeleton />
          </template>
          <div>
            <loading-overlay :isLoading="loadingChangesInEvent">
              <template #text>
                <span>Updating the calendar</span>
              </template>
              <template>
                <my-calendar
                  :calendarEvents="calendarEvents"
                  :editable="!userIsAParent || parentCanEdit"
                  @dateClick="dateClick"
                  @eventEdited="eventEdited"
                /> </template
            ></loading-overlay>
            <list-of-classes
              title="Classes"
              :list="classes"
              className="container mx-0 mx-md-3"
            ></list-of-classes>
          </div>
        </b-skeleton-wrapper>
      </b-container>
    </div>
    <b-button
      v-if="!userIsAParent || parentCanEdit"
      @click="goAddTask()"
      pill
      class="bg-main border-0 font-weight-bolder float-button-right rounded-circle p-1 mb-3 mr-2"
    >
      <i class="material-icons big-icon align-middle">add</i>
    </b-button>
  </div>
</template>

<script>
import { getClasses } from '@/module/services';
import { getCalendar, editProject, setEditTask } from '@/myTasks/services/';
import {
  adapterCalendarEvents,
  adapterToUpdateEventOrTask,
  adapterToUpdateProject,
} from '@/myTasks/adapters';
import { useSessionStore } from '@/common';
import { NavBar, LoadingOverlay } from '@/common/components';
import { getNumericDate } from '@/common/utils/Date';
import CalendarSkeleton from '@/myTasks/components/skeletons/CalendarSkeleton';
import MyCalendar from '@/myTasks/components/MyCalendar';
import ListOfClasses from '@/myTasks/components/ListOfClasses';
import { EVENT_TYPE } from '@/myTasks/constants';

export default {
  components: {
    NavBar,
    MyCalendar,
    CalendarSkeleton,
    ListOfClasses,
    LoadingOverlay,
  },
  data() {
    const sessionStore = useSessionStore();
    const noneClass = {
      name: 'None',
      color: 'c0c1f9',
    };
    return {
      loading: true,
      loadingChangesInEvent: false,
      classes: [],
      allEvents: [],
      calendarEvents: [],
      selectDate: null,
      selectHour: null,
      sessionStore,
      noneClass,
      getAllCalendarQuery: {
        all: true,
      },
    };
  },
  computed: {
    userIsAParent() {
      return this.sessionStore.userIsAParent;
    },
    childName() {
      return this.sessionStore.childName;
    },
    getChildId() {
      return this.userIsAParent ? this.sessionStore.childSelectedId : this.sessionStore.userId;
    },
    navBarTitle() {
      return this.userIsAParent ? `${this.childName}'s Calendar` : 'My Calendar';
    },
    parentCanEdit() {
      return this.sessionStore.getCurrentChild.parentCanEdit;
    },
  },
  methods: {
    dateClick(date) {
      const { selectDate, selectHour } = date;
      this.selectDate = selectDate;
      this.selectHour = selectHour;
    },
    goAddTask() {
      this.$router.push({
        name: 'new-calendar-task',
        params: {
          taskTypes: 'Events',
          calendarHour: this.selectHour || new Date().toString(),
          calendarDate: this.selectDate || getNumericDate(),
        },
      });
    },
    async getClassesAndTasks() {
      const childId = this.getChildId;
      this.classes = await getClasses(childId);
      this.classes.push(this.noneClass);
      this.allEvents = await getCalendar(childId, this.getAllCalendarQuery);
    },
    async refreshCalendar() {
      try {
        const childId = this.getChildId;
        const allEvents = await getCalendar(childId, this.getAllCalendarQuery);
        this.calendarEvents = adapterCalendarEvents(allEvents);
      } catch (error) {
        console.error(error);
      }
    },
    async eventEdited({ event }) {
      this.loadingChangesInEvent = true;
      let body = {};
      try {
        if (event.extendedProps.type === EVENT_TYPE.PROJECTS) {
          body = adapterToUpdateProject(event);
          await editProject(event.id, this.getChildId, body);
        } else {
          body = adapterToUpdateEventOrTask(event);
          await setEditTask(event.id, body);
        }
        await this.refreshCalendar();
      } catch (error) {
        console.error(error);
      } finally {
        this.loadingChangesInEvent = false;
      }
    },
  },
  async mounted() {
    this.loading = true;
    try {
      await this.getClassesAndTasks();
      this.calendarEvents = adapterCalendarEvents(this.allEvents);
    } catch (error) {
      console.error(error);
    } finally {
      this.loading = false;
    }
  },
};
</script>
